// FanPortalInteractionsSection — Fan Portal "Your interactions" block.
// Feb 17, 2026. Shows every silo the logged-in user has either starred or
// asked a question on. Includes per-row remove (X) button. Self-hides when
// the list is empty.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, X, ArrowSquareOut, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SiloArt from "@/components/SiloArt";

export default function FanPortalInteractionsSection() {
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/interactions/mine");
      setRows(r.data?.interactions || []);
    } catch { setRows([]); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    setRows((prev) => (prev || []).filter((r) => r.id !== id));
    try {
      await api.delete(`/interactions/${id}`);
      toast.success(`Removed ${name || "silo"} from your interactions.`);
    } catch (e) {
      toast.error("Couldn't remove right now.");
      load();
    }
  };

  if (rows === null) return null;
  if (rows.length === 0) return null;

  return (
    <section className="mt-12" data-testid="fan-interactions-section">
      <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Silos you're a fan of</h2>
      <p className="text-xs text-[#6B6A68] mt-1">
        Silos you starred, commented on, or were demoted from when a price changed. Tap the × to remove a row.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
        {rows.map((r) => (
          <div key={r.id}
            className="bg-white border hairline rounded-xl p-5 relative group"
            data-testid={`interaction-card-${r.silo_slug}`}>
            <button
              onClick={() => remove(r.id, r.silo_name)}
              className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#6B6A68] hover:bg-[#FDF1ED] hover:text-[#C16E5A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove from your interactions"
              data-testid={`interaction-remove-${r.silo_slug}`}
            >
              <X size={14} />
            </button>
            {r.silo_art_enabled && r.cube?.faces?.center?.image_id && (
              <SiloArt cube={r.cube} enabled className="w-12 h-12 mb-2" data-testid={`interaction-art-${r.silo_slug}`} />
            )}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
              {r.starred && <Star size={12} weight="fill" className="text-[#C16E5A]" />}
              {(r.kinds || []).includes("chat") && <ChatCircle size={12} weight="duotone" className="text-[#4A5D4E]" />}
              <span>{(r.kinds || []).join(" + ")}</span>
            </div>
            <h3 className="font-serif-display text-xl mt-1 text-[#1C1B1A]">{r.silo_name}</h3>
            {r.silo_tagline && <p className="text-xs text-[#6B6A68] mt-1 line-clamp-2">{r.silo_tagline}</p>}
            <div className="flex items-center gap-3 mt-4">
              <Link to={`/s/${r.silo_slug}`}
                className="text-xs px-3 py-2 rounded-md border hairline text-[#4A5D4E] inline-flex items-center gap-1.5 hover:bg-[#F0F1EE] transition-colors"
                data-testid={`interaction-open-${r.silo_slug}`}>
                Visit silo <ArrowSquareOut size={12} />
              </Link>
              {r.price_cents > 0 && (
                <span className="text-[11px] text-[#6B6A68]">{`$${Math.round(r.price_cents/100)}/mo to subscribe`}</span>
              )}
              {r.price_cents === 0 && (
                <span className="text-[11px] text-[#4A5D4E]">Free silo</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
